import React, { FC, useEffect, useRef, useState } from 'react';

import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaCheckbox, RaaDatePicker, RaaDialog, RaaModalPage, RaaSpinner } from 'raa-react';

import InputField from 'components/fieldComponents/raaInputField/RaaInputField';
import TextAreaField from 'components/fieldComponents/raaTextAreaField/RaaTextAreaField';
import ImageFileUploadInput, { ValidFileMimeTypesEnum } from 'components/utkast/imageFileUpload/ImageFileUpload';
import { RitningPostFormValues, SituationsplanFormSchema } from 'components/utkast/utkastRegistration/UtkastFormSchema';

import { DokumentationstypEnum, LicensinformationEnum, RitningVyEnum, RitningstypEnum } from 'enums';

import { usePostRitningMutation, usePutRitningMutation } from 'api/endpoints/dokumentationRitningSlice';
import { dokumentationSlice } from 'api/endpoints/dokumentationSlice';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { ErrorMessage, Form, Formik, FormikHelpers } from 'formik';
import { setApiValidationErrors } from 'utilities/formUtilities';
import { isFetchBaseQueryModelError } from 'utilities/typeGuards';

interface SituationsplanModalProps {
  resursId?: string;
  titel?: string;
  motivbeskrivning?: string;
  file: File;
  utkastId: string;
  height: number;
  width: number;
  vy?: RitningVyEnum;
  date?: Date;
  onSubmit?: () => void;
  onDelete?: (resursId: string, titel: string) => void;
  onCancel?: () => void;
}

const SituationsplanModal: FC<SituationsplanModalProps> = ({
  resursId,
  titel,
  motivbeskrivning,
  file,
  utkastId,
  height,
  width,
  vy,
  date,
  onDelete,
  onCancel,
  onSubmit
}) => {
  const dispatch = useAppDispatch();
  const { setDialog, unsetDialog, clearDialogStack } = useDialog();

  const [srcPreview, setSrcPreview] = useState('');
  const [imageInfo, setImageInfo] = useState({ height: 0, width: 0, filename: '', size: 0 });
  const [initialValues, setInitialValues] = useState<RitningPostFormValues>();
  const [selectedFile, setSelectedFile] = useState(file);

  const { requestStatus: utkastRequestStatus } = useAppSelector((state: RootState) => state.utkast);
  const { data: bebyggelseobjektDomaner } = useAppSelector((state: RootState) => state.bebyggelseobjektDomaner);

  const formikHelpersRef = useRef<FormikHelpers<RitningPostFormValues>>();

  const [postRitning, { isLoading: postRitningIsLoading, error: postRitningError, isSuccess: postRitningIsSuccess }] =
    usePostRitningMutation();

  const [putRitning, { isLoading: putRitningIsLoading, error: putRitningError, isSuccess: putRitningIsSuccess }] =
    usePutRitningMutation();

  useEffect(() => {
    const setInitialFormValues = () => {
      if (bebyggelseobjektDomaner) {
        const inCopyrightLicense = bebyggelseobjektDomaner.dokumentationlicenstyper?.find(
          (licenstyp) => licenstyp.namn === 'In Copyright'
        );

        if (!inCopyrightLicense) {
          throw new Error("Saknar id för licenstyp 'In Copyright'");
        }

        setInitialValues({
          titel: titel || '',
          bildfil: selectedFile || undefined,
          motivbeskrivning: motivbeskrivning || '',
          licens: inCopyrightLicense.id,
          licensinformation: LicensinformationEnum.COPYRIGHT_LANTMATERIET,
          tillkomstdatum: date || new Date(),
          vy: RitningVyEnum.OVERSIKT,
          godkand_spridning: false,
          ritningstyp: RitningstypEnum.SITUATIONSPLAN
        });
      }
    };

    const src = URL.createObjectURL(selectedFile);
    setSrcPreview(src);
    setImageInfo({ height, width, filename: selectedFile.name, size: selectedFile.size });
    setInitialFormValues();
  }, [selectedFile, height, width, date, titel, motivbeskrivning, vy, bebyggelseobjektDomaner]);

  useEffect(() => {
    const setFieldError = formikHelpersRef.current?.setFieldError;

    if (postRitningError && setFieldError && isFetchBaseQueryModelError(postRitningError)) {
      setApiValidationErrors(postRitningError.data, setFieldError);
    }

    if (putRitningError && setFieldError && isFetchBaseQueryModelError(putRitningError)) {
      setApiValidationErrors(putRitningError.data, setFieldError);
    }
  }, [postRitningError, putRitningError]);

  useEffect(() => {
    if (!formikHelpersRef.current) return;

    if (postRitningIsSuccess || putRitningIsSuccess) {
      unsetDialog();
      dispatch(dokumentationSlice.util.invalidateTags(['Dokumentation']));
      formikHelpersRef.current.resetForm();
      onSubmit && onSubmit();
    }
  }, [onSubmit, dispatch, postRitningIsSuccess, putRitningIsSuccess, unsetDialog, utkastId]);

  const deleteImage = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    if (onDelete && resursId) {
      onDelete(resursId, titel || '');
    }
  };

  const showWarningDialog = ({ proceed, cancel }: { proceed: () => void; cancel: () => void }) => {
    setDialog(RaaDialog, {
      cancelButtonText: 'avbryt',
      proceedButtonText: 'släng',
      header: 'Vill du slänga ändringarna?',
      proceed,
      cancel
    });
  };

  const submitForm = async (values: RitningPostFormValues, formikHelpers: FormikHelpers<RitningPostFormValues>) => {
    formikHelpersRef.current = formikHelpers;

    // Force current selected file
    values.bildfil = selectedFile;

    if (resursId) {
      await putRitning({ utkastId, resursId, ritningPut: values });
    } else {
      await postRitning({ utkastId, ritningPost: values });
    }
  };

  return (
    <RaaModalPage header="Situationsplan">
      {initialValues && (
        <Formik
          initialValues={initialValues}
          validationSchema={SituationsplanFormSchema}
          onSubmit={async (values: RitningPostFormValues, formikHelpers: FormikHelpers<RitningPostFormValues>) =>
            submitForm(values, formikHelpers)
          }
        >
          {({ setFieldValue, values, errors, touched, setFieldTouched, dirty }) => (
            <Form id="situationsplanForm" className="tw-flex tw-flex-col">
              <fieldset
                className="tw-grid tw-grid-cols-3 tw-border-b tw-border-raa-gray-3 tw-px-4 tw-pb-4 md:tw-px-6 md:tw-pb-6"
                disabled={putRitningIsLoading || postRitningIsLoading}
              >
                <div className="tw-col-span-3 tw-mb-4 md:tw-col-span-1 md:tw-mb-0">
                  <div className="tw-mx-2">
                    <img src={srcPreview} alt="Min bild" />
                  </div>
                  <div className="tw-mx-2 tw-mb-6 tw-break-words">
                    <p>{imageInfo.filename}</p>
                    <p>{`${imageInfo.height} x ${imageInfo.width} pixlar`}</p>
                    <p>{`${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB`}</p>
                  </div>
                  <div className="tw-mx-2">
                    <ImageFileUploadInput
                      displayAsButton={true}
                      name="bildfil"
                      labelText="Byt bildfil"
                      dokumentationstyp={DokumentationstypEnum.RITNING}
                      validFileMimeTypes={[ValidFileMimeTypesEnum.IMAGE_JPEG, ValidFileMimeTypesEnum.IMAGE_PNG]}
                      uploading={() => {
                        setDialog(RaaDialog, {
                          header: 'Laddar upp fil',
                          ariaDescribedBy: 'dialog-description',
                          children: (
                            <div className="tw-flex tw-justify-center tw-py-12">
                              <RaaSpinner size="3rem" />
                              <span id="dialog-description" className="tw-ml-2 tw-self-center">
                                Vänta...
                              </span>
                            </div>
                          )
                        });
                      }}
                      success={(newFile: File, height: number, width: number) => {
                        unsetDialog();
                        setSelectedFile(newFile);
                        const src = URL.createObjectURL(newFile);
                        setSrcPreview(src);
                        setImageInfo({ height, width, filename: newFile.name, size: newFile.size });
                      }}
                    />
                  </div>
                  <p className="tw-mt-6 tw-text-raa-error-red">{errors.bildfil && touched.bildfil && errors.bildfil}</p>
                </div>

                <div className="tw-col-span-3 tw-flex tw-flex-col tw-gap-4 tw-px-2 md:tw-col-span-2 md:tw-ml-4">
                  <div>
                    <InputField
                      name="titel"
                      placeholder="Ange bildens titel"
                      label="Titel *"
                      shouldValidateForm={true}
                    />
                  </div>
                  <div>
                    <TextAreaField
                      label="Bildtext (även ALT-text)"
                      multiple
                      name="motivbeskrivning"
                      classString="raa-textarea"
                      placeholder="Beskriv vad som visas på bilden"
                    />
                  </div>

                  <div>
                    <h3 className="tw-font-semibold">Licens</h3>
                    <p>Copyright Lantmäteriet</p>
                  </div>

                  <div className="-tw-mb-4">
                    <h3 className="tw-mb-[-4px] tw-font-semibold">Spridningstillstånd *</h3>
                    <RaaCheckbox
                      labelContent="Godkänd för spridning"
                      aria-errormessage={errors.godkand_spridning}
                      checked={values.godkand_spridning}
                      onChange={(event) => {
                        void setFieldValue('godkand_spridning', event.currentTarget.checked);
                      }}
                    />
                    <ErrorMessage name={'godkand_spridning'} component="p" className="tw-text-raa-error-red" />
                  </div>

                  <div>
                    <label className="raa-label" htmlFor="tillkomstdatum">
                      Bilddatum *
                    </label>
                    <RaaDatePicker
                      id="tillkomstdatum"
                      defaultValue={dayjs(date)}
                      onChange={async (value) => {
                        const dayjsValue = dayjs(value, 'YYYY-MM-DD').toString();
                        await setFieldTouched('tillkomstdatum');
                        void setFieldValue('tillkomstdatum', dayjsValue, true);
                      }}
                      disableFuture={true}
                    />

                    <ErrorMessage name={'tillkomstdatum'} component="p" className="tw-text-raa-error-red" />
                  </div>
                </div>

                <ErrorMessage name={'ritningstyp'} component="p" className="tw-text-raa-error-red" />
              </fieldset>

              <fieldset
                className="tw-mt-6 tw-flex tw-flex-col tw-justify-between tw-px-4 tw-pb-4 md:tw-flex-row md:tw-px-6 md:tw-pb-6"
                disabled={utkastRequestStatus === 'pending' || putRitningIsLoading || postRitningIsLoading}
              >
                <button
                  type="button"
                  className="raa-button raa-button-cancel"
                  onClick={() => {
                    if (dirty) {
                      showWarningDialog({
                        proceed: () => {
                          onCancel && onCancel();
                          clearDialogStack();
                        },
                        cancel: () => {
                          unsetDialog();
                        }
                      });
                    } else {
                      onCancel && onCancel();
                      unsetDialog();
                    }
                  }}
                >
                  avbryt
                </button>
                <div className="tw-flex tw-flex-col tw-gap-x-3 md:tw-flex-row">
                  <button
                    className={classNames('raa-button', { 'tw-hidden': !resursId })}
                    type="button"
                    onClick={(e) => deleteImage(e)}
                  >
                    Radera bilden
                  </button>
                  <button
                    type="submit"
                    className="raa-button raa-button-confirm tw-mt-2 md:tw-mt-0"
                    form="situationsplanForm"
                  >
                    {putRitningIsLoading || postRitningIsLoading ? <RaaSpinner size="1.5rem" /> : 'spara'}
                  </button>
                </div>
              </fieldset>
            </Form>
          )}
        </Formik>
      )}
    </RaaModalPage>
  );
};

export default SituationsplanModal;
